import React, {
  memo,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect
} from 'react';
import debounce from 'lodash/debounce';

import SearchInputIcon from 'static/images/sidebar/search.svg';
import SearchInverted from 'static/images/sidebar/search_inverted.svg';
import CloseIcon from 'static/images/sidebar/close.svg';
import LoadingIcon from 'static/images/sidebar/loading-gray.svg';
import { getResourceTo } from 'react/utils/utils';
import { getBreadcumbsMenus } from './utils';
import ResultsList from './ResultsList';
import { useMenus, useSidebar } from 'react/hooks/sidebar.hook';

function SearchContainer() {
  const { menus } = useMenus();
  const { isCollapsed, isHovered, isSearching, setIsSearching } = useSidebar();

  const [isLoading, setIsLoading] = useState(false);
  const [breadCumbMenusList, setBreadCumbMenusList] = useState([]);

  const inputRef = useRef(null);

  const clearSearch = useCallback(() => {
    setIsLoading(false);
    setIsSearching(false);
    setBreadCumbMenusList([]);

    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  }, []);

  const getMenusBreadCumbList = useCallback((textToSearch) => {
    setIsLoading(true);
    getBreadcumbsMenus({ menus, textToSearch })
      .then((data) => {
        setBreadCumbMenusList(data);
      })
      .catch((err) => {
        console.log(err);
        new Monet.Exception.ReturnError(null, null, err).console();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [menus]);

  const handleKeyUp = useMemo(() => debounce((value) => {
    if (!value.length) {
      clearSearch();
      return;
    }
    getMenusBreadCumbList(value);
    setIsSearching(true);
  }, 500), [getMenusBreadCumbList]);

  const getInputIcon = useCallback(() => {
    if (isLoading) {
      return (
        <img src={LoadingIcon} className="loading-icon" alt="Input Loading Icon" />
      );
    }

    if (isSearching) {
      return (
        <img
          src={CloseIcon}
          className="close-icon"
          alt="Input Close Icon"
          onClick={clearSearch}
        />
      );
    }

    return (
      <img src={SearchInputIcon} alt="Input Search Icon" />
    );
  }, [isLoading, isSearching]);

  useEffect(() => {
    if (isCollapsed && !isSearching) {
      clearSearch();
    }

    if (!isCollapsed) {
      inputRef.current.focus();
    }
  }, [isCollapsed, isHovered]);

  return (
    <div className="search-container">
      {(isCollapsed && !isHovered && !isSearching) ? (
        <button type="button">
          <img src={SearchInverted} alt="" />
        </button>
      ) : (
        <div className="input-container">
          <input
            data-testid="search-menu-input"
            ref={inputRef}
            type="text"
            placeholder={getResourceTo('sidebarMenu.search.InputPlaceholder')}
            onKeyUp={(e) => handleKeyUp(e.target.value)}
          />
          {getInputIcon()}
        </div>
      )}
      {(isSearching && !isLoading) && (
        <ResultsList
          clearSearch={clearSearch}
          breadCumbMenusList={breadCumbMenusList}
        />
      )}
    </div>
  );
}

export default memo(SearchContainer);
